import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { Accordion as SparkyAccordion, ButtonLink, Heading, Stack, Text, TextLink } from '@eneco/sparky';
import type { HeadingProps } from '@eneco/sparky/types';

import type { AccordionContent, AccordionItem } from './accordion-content.types';

const headingSizes: Record<string, HeadingProps['size']> = {
  headingDefault: { initial: 'S', md: 'M' },
  headingHero: { initial: 'M', md: 'XL' },
  headingSub: { initial: 'XS', md: 'S' },
};

/**
 * `Accordion` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/Accordion/Accordion.tsx`):
 * an optional title and intro text, a Sparky `Accordion` of question/answer items (each with an
 * optional button and a text link), then an optional closing text and link. Drops the Sitecore
 * Experience Editor `wrap()` calls — this lane never runs inside the Experience Editor.
 *
 * Both adapters hand each item over with the same `question`/`answer` names; see
 * `accordion-content.types.ts`. A Sanity item has `_key` where a Sitecore one has `id`.
 */
const Accordion: FC = () => {
  const { adapter } = useCms();
  const content = useContent<AccordionContent>();

  const headingLevel = (useSetting('headingLevel') as HeadingProps['as']) ?? 'h2';
  const headingSize = headingSizes[useSetting('headingSize') ?? 'headingDefault'];

  const introHtml = adapter.toRichText(content.content);
  const closingHtml = adapter.toRichText(content.text);
  const link = linkWithUrl(content.link);
  const items = (content.items ?? []).filter(item => item.question);

  return (
    <Stack gap="6">
      {content.title && (
        <Heading size={headingSize} as={headingLevel} color="inherit">
          {content.title}
        </Heading>
      )}
      {introHtml && (
        <Text>
          <RichTextComponent html={introHtml} />
        </Text>
      )}
      <Stack.Item>
        <SparkyAccordion>
          {items.map(item => (
            <AccordionItemBlock key={item.id ?? item._key} item={item} toRichText={adapter.toRichText} />
          ))}
        </SparkyAccordion>
      </Stack.Item>
      {closingHtml && (
        <Text>
          <RichTextComponent html={closingHtml} />
        </Text>
      )}
      {link?.href && link.text && (
        <TextLink href={link.href} target={link.target} emphasis="medium">
          {link.text}
        </TextLink>
      )}
    </Stack>
  );
};

const AccordionItemBlock: FC<{
  item: AccordionItem;
  toRichText: (raw: unknown) => string | undefined;
}> = ({ item, toRichText }) => {
  const answerHtml = toRichText(item.answer);
  const buttonLink = linkWithUrl(item.buttonLink);
  const link = linkWithUrl(item.link);

  return (
    <SparkyAccordion.Item id={item.id} heading={item.question ?? ''}>
      <Stack direction="column" alignY="center" alignX="start" gap="4">
        {answerHtml && (
          <Text as="p">
            <RichTextComponent html={answerHtml} />
          </Text>
        )}
        {buttonLink?.href && buttonLink.text && (
          <ButtonLink href={buttonLink.href} target={buttonLink.target}>
            {buttonLink.text}
          </ButtonLink>
        )}
        {link?.href && link.text && (
          <TextLink href={link.href} target={link.target} emphasis="high">
            {link.text}
          </TextLink>
        )}
      </Stack>
    </SparkyAccordion.Item>
  );
};

export default Accordion;
